import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { TextField } from '@/shared/forms/TextField';
import { Button } from '@/shared/ui/Button';

import { useForgotPassword, useResetPassword } from '../hooks/usePasswordReset';
import { resetSchema } from '../validation/reset.schema';

export function ResetPasswordForm({ email, onReset }) {
  const reset = useResetPassword();
  const resend = useForgotPassword();
  const [resent, setResent] = useState(false);
  const form = useForm({
    resolver: zodResolver(resetSchema),
    defaultValues: { otp: '', password: '', passwordConfirm: '' },
    mode: 'onBlur',
  });

  async function onResend() {
    setResent(false);
    form.clearErrors('root');

    try {
      await resend.mutateAsync({ email });
      form.setValue('otp', '');
      form.clearErrors('otp');
      setResent(true);
    } catch (error) {
      form.setError('root', {
        message:
          error.status === 429
            ? 'Too many attempts. Try again in a minute.'
            : error.message || 'Could not send a new code. Please try again.',
      });
    }
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setResent(false);

    try {
      await reset.mutateAsync({ email, otp: values.otp, password: values.password });
      onReset();
    } catch (error) {
      if (error.status === 422 && error.details?.otp) {
        form.setError('otp', { message: error.details.otp[0] });
        return;
      }
      if (error.status === 422 && error.details?.password) {
        form.setError('password', { message: error.details.password[0] });
        return;
      }
      form.setError('root', {
        message:
          error.status === 429
            ? 'Too many attempts. Try again in a minute.'
            : error.message || 'Could not reset your password. Please try again.',
      });
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-5">
      <TextField
        label="Verification code"
        required
        inputMode="numeric"
        autoComplete="one-time-code"
        error={form.formState.errors.otp?.message}
        {...form.register('otp')}
      />
      <TextField
        label="New password"
        required
        type="password"
        autoComplete="new-password"
        error={form.formState.errors.password?.message}
        {...form.register('password')}
      />
      <TextField
        label="Confirm new password"
        required
        type="password"
        autoComplete="new-password"
        error={form.formState.errors.passwordConfirm?.message}
        {...form.register('passwordConfirm')}
      />
      {form.formState.errors.root && (
        <p
          role="alert"
          className="rounded-lg bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300"
        >
          {form.formState.errors.root.message}
        </p>
      )}
      {resent && !form.formState.errors.root && (
        <p
          role="status"
          className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300"
        >
          New code sent — check your inbox.
        </p>
      )}
      <Button type="submit" loading={reset.isPending} className="w-full">
        Reset password
      </Button>
      <Button type="button" variant="ghost" loading={resend.isPending} onClick={onResend}>
        Resend code
      </Button>
    </form>
  );
}
